import React, { useEffect, useState, useCallback, useMemo } from "react";
import { RefreshCw, Plus, Minus, X, ShoppingCart, Trash2, Pencil, Search, BarChart3 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

function ItemModal({ item, onClose, onSaved, authHeaders }) {
  const isEdit = !!item;
  const [form, setForm] = useState({ name: item?.name || "", category: item?.category || "", price: item?.price ?? "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function submit() {
    if (!form.name || form.price === "") { setError("Name and price are required."); return; }
    setSaving(true); setError("");
    try {
      const res = await fetch(`${API}/pos/items${isEdit ? `/${item._id}` : ""}`, {
        method: isEdit ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(form),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      onSaved(json.data); onClose();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
      <div className="bg-white/80 backdrop-blur-2xl border border-white/50 rounded-2xl shadow-2xl shadow-black/10 w-full max-w-sm">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">{isEdit ? "Edit Item" : "New Item"}</h2>
          <button onClick={onClose} className="p-1.5 -m-1.5"><X size={18} className="text-gray-500" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Name *</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Category</label>
              <input value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} placeholder="e.g. Beverages"
                className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Price (₹) *</label>
              <input type="number" min="0" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
          </div>
          {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>}
        </div>
        <div className="px-6 py-4 border-t border-gray-100">
          <button onClick={submit} disabled={saving}
            className="w-full py-3.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white
              font-semibold text-sm shadow-sm hover:from-amber-600 hover:to-orange-600 disabled:opacity-60 transition-all">
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Add Item"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ManageItems({ authHeaders }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalFor, setModalFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/pos/items?all=true`, { headers: authHeaders() });
      const json = await res.json();
      setItems(json.data || []);
    } catch {}
    setLoading(false);
    // eslint-disable-next-line
  }, []);
  useEffect(() => { load(); }, [load]);

  async function toggleActive(item) {
    const res = await fetch(`${API}/pos/items/${item._id}`, {
      method: "PATCH", headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ active: !item.active }),
    });
    const json = await res.json();
    if (json.success) setItems(prev => prev.map(i => i._id === item._id ? json.data : i));
  }

  async function remove(id) {
    if (!window.confirm("Delete this item?")) return;
    const res = await fetch(`${API}/pos/items/${id}`, { method: "DELETE", headers: authHeaders() });
    if (res.ok) setItems(prev => prev.filter(i => i._id !== id));
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => setModalFor("new")}
          className="flex items-center gap-2 text-sm font-medium text-white bg-gradient-to-r from-amber-500 to-orange-500
            px-4 py-2 rounded-xl shadow-sm hover:from-amber-600 hover:to-orange-600 transition-all">
          <Plus size={15} /> New Item
        </button>
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-50">
        {loading ? (
          <div className="text-center py-12 text-gray-400"><RefreshCw size={20} className="animate-spin mx-auto mb-2" /> Loading…</div>
        ) : items.length === 0 ? (
          <p className="text-center py-12 text-gray-400">No items in the catalog yet</p>
        ) : items.map(i => (
          <div key={i._id} className="flex items-center justify-between px-4 py-3.5">
            <div className="min-w-0">
              <p className="font-medium text-gray-900">{i.name}</p>
              <p className="text-xs text-gray-400">{i.category || "Uncategorized"} · ₹{i.price}</p>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button onClick={() => toggleActive(i)}
                className={`text-[10.5px] font-medium px-2 py-1 rounded-full border ${i.active ? "bg-green-50 text-green-700 border-green-200" : "bg-gray-100 text-gray-500 border-gray-200"}`}>
                {i.active ? "Active" : "Inactive"}
              </button>
              <button onClick={() => setModalFor(i)} className="p-2 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100"><Pencil size={13} /></button>
              <button onClick={() => remove(i._id)} className="p-2 rounded-lg bg-red-50 text-red-500 hover:bg-red-100"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>
      {modalFor && (
        <ItemModal item={modalFor === "new" ? null : modalFor} onClose={() => setModalFor(null)}
          onSaved={saved => setItems(prev => modalFor === "new" ? [...prev, saved] : prev.map(i => i._id === saved._id ? saved : i))}
          authHeaders={authHeaders} />
      )}
    </div>
  );
}

function CheckoutModal({ cart, total, onClose, onDone, authHeaders }) {
  const [paymentType, setPaymentType] = useState("cash");
  const [bookings, setBookings] = useState([]);
  const [bookingSearch, setBookingSearch] = useState("");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [guestName, setGuestName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (paymentType === "room_charge" && bookings.length === 0) {
      fetch(`${API}/pos/active-bookings`, { headers: authHeaders() })
        .then(r => r.json()).then(j => setBookings(j.data || [])).catch(() => {});
    }
    // eslint-disable-next-line
  }, [paymentType]);

  const filteredBookings = bookings.filter(b =>
    !bookingSearch || b.guestName.toLowerCase().includes(bookingSearch.toLowerCase()) || b.unitRoomNumber?.includes(bookingSearch));

  async function submit() {
    if (paymentType === "room_charge" && !selectedBooking) { setError("Select a room to charge."); return; }
    setSaving(true); setError("");
    try {
      const res = await fetch(`${API}/pos/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          items: cart.map(c => ({ itemId: c._id, qty: c.qty })),
          paymentType,
          bookingId: selectedBooking?._id,
          guestName,
        }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      onDone(json.data);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/30 backdrop-blur-sm">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl w-full sm:max-w-sm max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Checkout — ₹{total.toLocaleString("en-IN")}</h2>
          <button onClick={onClose} className="p-1.5 -m-1.5"><X size={18} className="text-gray-500" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {[["cash","Cash"],["card","Card"],["upi","UPI"],["room_charge","Room Charge"]].map(([v, label]) => (
              <button key={v} onClick={() => setPaymentType(v)}
                className={`py-3.5 rounded-xl text-sm font-semibold border-2 transition-colors ${
                  paymentType === v ? "border-amber-500 bg-amber-50 text-amber-700" : "border-gray-100 text-gray-500"
                }`}>
                {label}
              </button>
            ))}
          </div>

          {paymentType === "room_charge" ? (
            <div>
              <div className="relative mb-2">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input value={bookingSearch} onChange={e => setBookingSearch(e.target.value)} placeholder="Search guest or room…"
                  className="w-full border border-gray-200 rounded-xl pl-9 pr-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
              </div>
              <div className="max-h-40 overflow-y-auto space-y-1.5">
                {filteredBookings.length === 0 && <p className="text-xs text-gray-400 text-center py-3">No in-house guests found</p>}
                {filteredBookings.map(b => (
                  <button key={b._id} onClick={() => setSelectedBooking(b)}
                    className={`w-full text-left px-3 py-3 rounded-xl border text-sm ${
                      selectedBooking?._id === b._id ? "border-amber-500 bg-amber-50" : "border-gray-100"
                    }`}>
                    <span className="font-medium text-gray-900">{b.guestName}</span>
                    <span className="text-gray-400"> · Room {b.unitRoomNumber} · {b.bookingRef}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Guest Name (optional)</label>
              <input value={guestName} onChange={e => setGuestName(e.target.value)} placeholder="Walk-in guest"
                className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
          )}
          {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>}
        </div>
        <div className="px-6 py-4 border-t border-gray-100">
          <button onClick={submit} disabled={saving}
            className="w-full py-4 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white
              font-bold text-base shadow-sm hover:from-amber-600 hover:to-orange-600 disabled:opacity-60 transition-all">
            {saving ? "Placing Order…" : `Confirm — ₹${total.toLocaleString("en-IN")}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function TakeOrder({ authHeaders }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("All");
  const [cart, setCart] = useState({}); // itemId -> qty
  const [showCheckout, setShowCheckout] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/pos/items`, { headers: authHeaders() });
      const json = await res.json();
      setItems(json.data || []);
    } catch {}
    setLoading(false);
    // eslint-disable-next-line
  }, []);
  useEffect(() => { load(); }, [load]);

  const categories = useMemo(() => ["All", ...new Set(items.map(i => i.category || "Uncategorized"))], [items]);
  const visibleItems = items.filter(i => cat === "All" || (i.category || "Uncategorized") === cat);

  function addToCart(item) { setCart(c => ({ ...c, [item._id]: (c[item._id] || 0) + 1 })); }
  function changeQty(itemId, delta) {
    setCart(c => {
      const next = Math.max(0, (c[itemId] || 0) + delta);
      const copy = { ...c };
      if (next === 0) delete copy[itemId]; else copy[itemId] = next;
      return copy;
    });
  }

  const cartList = Object.entries(cart).map(([id, qty]) => ({ ...items.find(i => i._id === id), qty })).filter(c => c._id);
  const total = cartList.reduce((s, c) => s + c.price * c.qty, 0);
  const cartCount = cartList.reduce((s, c) => s + c.qty, 0);

  if (confirmed) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-3xl">✓</span>
        </div>
        <h3 className="text-lg font-bold text-gray-900">Order Placed</h3>
        <p className="text-sm text-gray-500 mt-1">₹{confirmed.total.toLocaleString("en-IN")} · {confirmed.paymentType === "room_charge" ? "Charged to room" : `Paid via ${confirmed.paymentType}`}</p>
        <button onClick={() => setConfirmed(null)}
          className="mt-6 px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold text-sm">
          New Order
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-24">
      <div className="flex gap-2 overflow-x-auto pb-1">
        {categories.map(c => (
          <button key={c} onClick={() => setCat(c)}
            className={`px-4 py-2.5 min-h-[44px] rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${
              cat === c ? "bg-amber-500 text-white" : "bg-gray-100 text-gray-600"
            }`}>
            {c}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400"><RefreshCw size={20} className="animate-spin mx-auto mb-2" /> Loading…</div>
      ) : visibleItems.length === 0 ? (
        <p className="text-center py-12 text-gray-400 bg-white rounded-2xl border border-gray-100">No items available</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {visibleItems.map(i => {
            const qty = cart[i._id] || 0;
            return (
              <div key={i._id} className="bg-white rounded-2xl border border-gray-100 p-4">
                <p className="font-semibold text-gray-900 truncate">{i.name}</p>
                <p className="text-sm text-gray-500 mb-3">₹{i.price}</p>
                {qty === 0 ? (
                  <button onClick={() => addToCart(i)}
                    className="w-full py-3 rounded-xl bg-amber-50 text-amber-700 font-semibold text-sm hover:bg-amber-100">
                    Add
                  </button>
                ) : (
                  <div className="flex items-center justify-between bg-amber-500 rounded-xl">
                    <button onClick={() => changeQty(i._id, -1)} className="p-3 text-white"><Minus size={16} /></button>
                    <span className="text-white font-bold">{qty}</span>
                    <button onClick={() => changeQty(i._id, 1)} className="p-3 text-white"><Plus size={16} /></button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {cartCount > 0 && (
        <div className="fixed bottom-16 sm:bottom-4 left-0 right-0 px-4 z-30">
          <button onClick={() => setShowCheckout(true)}
            className="w-full max-w-lg mx-auto flex items-center justify-between px-5 py-4 rounded-2xl
              bg-gray-900 text-white shadow-2xl">
            <span className="flex items-center gap-2 font-semibold text-sm">
              <ShoppingCart size={16} /> {cartCount} item{cartCount !== 1 ? "s" : ""}
            </span>
            <span className="font-bold">₹{total.toLocaleString("en-IN")} → Checkout</span>
          </button>
        </div>
      )}

      {showCheckout && (
        <CheckoutModal cart={cartList} total={total} authHeaders={authHeaders}
          onClose={() => setShowCheckout(false)}
          onDone={order => { setConfirmed(order); setCart({}); setShowCheckout(false); }} />
      )}
    </div>
  );
}

export default function POSPage() {
  const { authHeaders, role } = useAuth();
  const [tab, setTab] = useState("order");
  const canManageItems = role === "owner" || role === "manager";

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Point of Sale</h1>
        {canManageItems && (
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
            <button onClick={() => setTab("order")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "order" ? "bg-white shadow-sm text-gray-900" : "text-gray-500"}`}>
              Take Order
            </button>
            <button onClick={() => setTab("items")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "items" ? "bg-white shadow-sm text-gray-900" : "text-gray-500"}`}>
              Manage Items
            </button>
            <button onClick={() => setTab("reports")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "reports" ? "bg-white shadow-sm text-gray-900" : "text-gray-500"}`}>
              Reports
            </button>
          </div>
        )}
      </div>

      {tab === "order" ? <TakeOrder authHeaders={authHeaders} />
        : tab === "items" ? <ManageItems authHeaders={authHeaders} />
        : <SalesReport authHeaders={authHeaders} />}
    </div>
  );
}

// Daily revenue rollup + best-sellers, last 30 days.
function SalesReport({ authHeaders }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const from = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
    fetch(`${API}/pos/reports?from=${from}`, { headers: authHeaders() })
      .then(r => r.json()).then(j => setData(j.data)).catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  if (loading) return <div className="text-center py-12 text-gray-400"><RefreshCw size={20} className="animate-spin mx-auto mb-2" /> Loading…</div>;
  if (!data) return <p className="text-center py-12 text-gray-400">Couldn't load report</p>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <p className="text-xs text-gray-400 mb-1">Total Revenue (30d)</p>
          <p className="text-2xl font-bold text-gray-900">₹{data.totalRevenue.toLocaleString("en-IN")}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <p className="text-xs text-gray-400 mb-1">Total Orders (30d)</p>
          <p className="text-2xl font-bold text-gray-900">{data.totalOrders}</p>
        </div>
      </div>

      <div>
        <h2 className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 mb-3">
          <BarChart3 size={15} className="text-amber-500" /> Daily Revenue
        </h2>
        <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-50">
          {data.dailyRevenue.length === 0 ? (
            <p className="text-center py-8 text-gray-400 text-sm">No sales in this period</p>
          ) : data.dailyRevenue.map(d => (
            <div key={d.date} className="flex items-center justify-between px-4 py-2.5 text-sm">
              <span className="text-gray-600">{new Date(d.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>
              <span className="text-gray-400">{d.orders} order{d.orders !== 1 ? "s" : ""}</span>
              <span className="font-semibold text-gray-900">₹{d.revenue.toLocaleString("en-IN")}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Best Sellers</h2>
        <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-50">
          {data.topItems.length === 0 ? (
            <p className="text-center py-8 text-gray-400 text-sm">No items sold yet</p>
          ) : data.topItems.map((item, i) => (
            <div key={item.name} className="flex items-center justify-between px-4 py-2.5 text-sm">
              <span className="text-gray-900">{i + 1}. {item.name}</span>
              <span className="text-gray-400">{item.qty} sold</span>
              <span className="font-semibold text-gray-900">₹{item.revenue.toLocaleString("en-IN")}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
